const ROOT = import.meta.env.BASE_URL.replace(/\/$/, "");
const STORAGE_KEY = "monitor_dodf_session";

type PesquisaFila = {
  id: number;
  termo: string;
  status: "pendente" | "processando" | "concluida" | "parcial" | "falhou" | "cancelada";
  fila_posicao?: number | null;
  pesquisas_a_frente?: number;
  fila_total_aguardando?: number;
  capacidade_simultanea_atual?: number;
  duracao_tipica_segundos?: number;
  estimativa_inicio_segundos?: number | null;
  estimativa_conclusao_segundos?: number | null;
};

function tokenAtual() {
  try {
    const bruto = localStorage.getItem(STORAGE_KEY);
    if (!bruto) return "";
    return String(JSON.parse(bruto)?.access_token || "");
  } catch {
    return "";
  }
}

function minutos(segundos?: number | null) {
  if (segundos === null || segundos === undefined) return "estimativa indisponível";
  if (segundos <= 30) return "menos de 1 min";
  const m = Math.max(1, Math.ceil(segundos / 60));
  return `cerca de ${m} min`;
}

function instalarEstilos() {
  if (document.getElementById("fila-historica-estilos")) return;
  const style = document.createElement("style");
  style.id = "fila-historica-estilos";
  style.textContent = `
    .historical-queue-box{margin:12px 0 16px;padding:14px;border:1px solid #dbeafe;border-radius:12px;background:#f8fbff;color:#0f172a}
    .historical-queue-head{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;flex-wrap:wrap}
    .historical-queue-head strong{font-size:14px}.historical-queue-head span{font-size:12px;color:#475569}
    .historical-queue-list{display:grid;gap:8px;margin-top:10px}.historical-queue-item{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:4px 12px;padding:10px 12px;border-radius:9px;background:#fff;border:1px solid #e2e8f0}
    .historical-queue-item strong{font-size:13px;overflow-wrap:anywhere}.historical-queue-item b{font-size:12px;color:#1d4ed8}.historical-queue-item small{grid-column:1/-1;color:#64748b;line-height:1.4}
    .historical-queue-free{margin-top:8px;font-size:12px;color:#166534}.historical-queue-error{margin-top:8px;font-size:12px;color:#92400e}
    @media(max-width:600px){.historical-queue-box{padding:12px}.historical-queue-item{grid-template-columns:1fr}.historical-queue-item small{grid-column:auto}}
  `;
  document.head.appendChild(style);
}

function encontrarSecao() {
  const titulos = Array.from(document.querySelectorAll("h2"));
  const titulo = titulos.find((el) => el.textContent?.trim() === "Pesquisas históricas");
  return titulo?.closest("section.panel") as HTMLElement | null;
}

function garantirBox(secao: HTMLElement) {
  let box = secao.querySelector(".historical-queue-box") as HTMLDivElement | null;
  if (!box) {
    box = document.createElement("div");
    box.className = "historical-queue-box";
    box.setAttribute("aria-live", "polite");
    const head = secao.querySelector(".section-head");
    if (head?.nextSibling) secao.insertBefore(box, head.nextSibling);
    else secao.appendChild(box);
  }
  return box;
}

function renderizar(box: HTMLDivElement, pesquisas: PesquisaFila[]) {
  const ativas = pesquisas
    .filter((p) => p.status === "pendente" || p.status === "processando")
    .sort((a, b) => a.id - b.id);
  const capacidade = pesquisas.find((p) => p.capacidade_simultanea_atual)?.capacidade_simultanea_atual ?? 1;
  const tipica = pesquisas.find((p) => p.duracao_tipica_segundos)?.duracao_tipica_segundos;

  const cabecalho = `<div class="historical-queue-head"><div><strong>Fila da Linha do Tempo</strong><br><span>Processamento protegido: ${capacidade} pesquisa por vez. As demais aguardam sem sobrecarregar o servidor.</span></div>${tipica ? `<span>Duração típica recente: ${minutos(tipica)}</span>` : ""}</div>`;

  if (!ativas.length) {
    box.innerHTML = `${cabecalho}<div class="historical-queue-free">Fila livre neste momento. Uma nova pesquisa pode começar no próximo ciclo do worker.</div>`;
    return;
  }

  const itens = ativas.map((p) => {
    if (p.status === "processando") {
      return `<div class="historical-queue-item"><strong>#${p.id} · ${escapeHtml(p.termo)}</strong><b>Em processamento</b><small>Estimativa para concluir: ${minutos(p.estimativa_conclusao_segundos)}.</small></div>`;
    }
    const frente = p.pesquisas_a_frente ?? 0;
    const posicao = p.fila_posicao ?? 1;
    const detalhe = frente > 0 ? `${frente} pesquisa(s) estão à frente.` : "É a próxima pesquisa a iniciar.";
    return `<div class="historical-queue-item"><strong>#${p.id} · ${escapeHtml(p.termo)}</strong><b>Posição ${posicao} na fila</b><small>${detalhe} Início estimado em ${minutos(p.estimativa_inicio_segundos)}; conclusão estimada em ${minutos(p.estimativa_conclusao_segundos)}.</small></div>`;
  }).join("");

  box.innerHTML = `${cabecalho}<div class="historical-queue-list">${itens}</div>`;
}

function escapeHtml(valor: string) {
  const mapa: Record<string, string> = {"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"};
  return valor.replace(/[&<>"']/g, (c) => mapa[c] || c);
}

let carregando = false;
let ultimoCarregamento = 0;

async function atualizar() {
  const secao = encontrarSecao();
  if (!secao || carregando) return;
  const agora = Date.now();
  if (agora - ultimoCarregamento < 1500) return;
  ultimoCarregamento = agora;
  instalarEstilos();
  const box = garantirBox(secao);
  const token = tokenAtual();
  if (!token) {
    box.innerHTML = `<div class="historical-queue-head"><strong>Fila da Linha do Tempo</strong></div><div class="historical-queue-error">Entre novamente para consultar a posição da fila.</div>`;
    return;
  }
  carregando = true;
  try {
    const resposta = await fetch(`${ROOT}/api/pesquisas-historicas?limite=50`, {headers:{Authorization:`Bearer ${token}`}});
    if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
    const pesquisas = await resposta.json() as PesquisaFila[];
    renderizar(box, pesquisas);
  } catch {
    box.innerHTML = `<div class="historical-queue-head"><strong>Fila da Linha do Tempo</strong></div><div class="historical-queue-error">Não foi possível atualizar a fila agora. A pesquisa continua preservada no servidor.</div>`;
  } finally {
    carregando = false;
  }
}

const observer = new MutationObserver(() => { void atualizar(); });
observer.observe(document.documentElement, {childList:true, subtree:true});
window.setInterval(() => { if (encontrarSecao()) void atualizar(); }, 5000);
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => void atualizar());
else void atualizar();
